import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Battery,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Database,
  Download,
  Gamepad2,
  Info,
  Layers,
  RefreshCw,
  Wifi,
} from 'lucide-react';
import {
  analyticsCsv,
  customSnapshot,
  formatDuration,
  formatInt,
  formatMb,
  formatPct,
  formatRange,
  SAMPLE_END,
  SAMPLE_START,
  snapshotFor,
  WEEK_END,
  WEEK_START,
  type AnalyticsPeriod,
  type AnalyticsSnapshot,
} from '../../data/analytics-mock';
import { AnalyticsArt } from './AnalyticsArt';
import { DonutChart, Sparkline, SYNC_SERIES, SyncChart, UsageChart, type DonutSlice } from './AnalyticsCharts';
import './analytics.css';

type TrafficMode = 'calls' | 'data';
type ModuleMode = 'time' | 'sessions';

const INTRO = 'Understand your MockLoc activity, network usage and sync health.';
const PERIODS: Array<{ id: AnalyticsPeriod; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: 'custom', label: 'Custom' },
];

export interface AnalyticsViewProps {
  /** True while analytics requests are in flight. Omit to preview skeletons once. */
  loading?: boolean;
  /**
   * Leave unset to use the built-in sample for the selected range.
   * Pass null when the API returns an empty range.
   */
  data?: AnalyticsSnapshot | null;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function usePreviewLoading(enabled: boolean): boolean {
  const [loading, setLoading] = useState(() => enabled && !prefersReducedMotion());

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    if (prefersReducedMotion()) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const id = window.setTimeout(() => setLoading(false), 680);
    return () => window.clearTimeout(id);
  }, [enabled]);

  return loading;
}

function Skel({ className = '' }: { className?: string }) {
  return <span className={`an-skel ${className}`} />;
}

function Hint({ label, text }: { label: string; text: string }) {
  return (
    <span className="an-hint">
      <button type="button" className="an-hint__btn" aria-label={label}>
        <Info size={14} strokeWidth={2.4} aria-hidden="true" />
      </button>
      <span className="an-hint__pop" role="tooltip">
        {text}
      </span>
    </span>
  );
}

function Delta({ value }: { value: number }) {
  const up = value >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <p className={`an-delta ${up ? 'is-up' : 'is-down'}`}>
      <Icon size={14} aria-hidden="true" />
      <span>{`${value > 0 ? '+' : ''}${value.toFixed(1)}%`}</span>
      <span className="an-delta__rest">vs previous period</span>
    </p>
  );
}

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function KpiSkeleton() {
  return (
    <div className="an-kpis" data-testid="skeleton-kpis" aria-hidden="true">
      {[0, 1, 2, 3].map((key) => (
        <div key={key} className="an-card an-kpi">
          <Skel className="an-skel--ico" />
          <Skel className="an-skel--line" />
          <Skel className="an-skel--value" />
          <Skel className="an-skel--short" />
        </div>
      ))}
    </div>
  );
}

function CardSkeleton({ testId, plot = false }: { testId: string; plot?: boolean }) {
  return (
    <section className="an-card" data-testid={testId} aria-hidden="true">
      <Skel className="an-skel--title" />
      <Skel className="an-skel--line" />
      {plot ? <Skel className="an-skel--plot" /> : <Skel className="an-skel--block" />}
    </section>
  );
}

function Skeletons() {
  return (
    <>
      <KpiSkeleton />
      <div className="an-split">
        <CardSkeleton testId="skeleton-usage" plot />
        <CardSkeleton testId="skeleton-traffic" />
      </div>
      <div className="an-split an-split--even">
        <CardSkeleton testId="skeleton-modules" />
        <CardSkeleton testId="skeleton-sync" plot />
      </div>
      <div className="an-split an-split--pair">
        <CardSkeleton testId="skeleton-resources" />
        <CardSkeleton testId="skeleton-insights" />
      </div>
    </>
  );
}

function EmptyState({ rangeLabel, onSample }: { rangeLabel: string; onSample?: () => void }) {
  return (
    <section className="an-empty" data-testid="analytics-empty">
      <AnalyticsArt />
      <h2>No activity in this range</h2>
      <p>No mocking, syncs, or network use landed between {rangeLabel}.</p>
      {onSample && (
        <button type="button" className="an-primary" onClick={onSample}>
          View sample week
        </button>
      )}
    </section>
  );
}

function AnalyticsBody({
  snapshot,
  traffic,
  moduleMode,
  syncOpen,
  onTraffic,
  onModuleMode,
  onToggleSync,
}: {
  snapshot: AnalyticsSnapshot;
  traffic: TrafficMode;
  moduleMode: ModuleMode;
  syncOpen: boolean;
  onTraffic: (mode: TrafficMode) => void;
  onModuleMode: (mode: ModuleMode) => void;
  onToggleSync: () => void;
}) {
  const { kpis, resources, insights } = snapshot;
  const totalData = resources.mobileMb + resources.wifiMb;
  const slices: DonutSlice[] = snapshot.traffic.map((slice) => ({
    id: slice.id,
    label: slice.label,
    tone: slice.tone,
    value: traffic === 'calls' ? slice.calls : slice.dataMb,
    display: traffic === 'calls' ? formatInt(slice.calls) : formatMb(slice.dataMb),
  }));
  const trafficTotal = slices.reduce((sum, slice) => sum + slice.value, 0);
  const sessionTotal = snapshot.modules.reduce((sum, row) => sum + row.sessions, 0);
  const moduleShares = snapshot.modules.map((row) =>
    moduleMode === 'time' ? row.timeShare : sessionTotal > 0 ? (row.sessions / sessionTotal) * 100 : 0
  );
  const maxShare = Math.max(...moduleShares, 0);

  return (
    <>
      <div className="an-kpis">
        <article className="an-card an-kpi">
          <div className="an-kpi__top">
            <span className="an-ico an-ico--green">
              <Wifi size={18} aria-hidden="true" />
            </span>
            <Sparkline values={kpis.networkSpark} tone="green" />
          </div>
          <p className="an-kpi__label">Network used</p>
          <p className="an-kpi__value">{formatMb(kpis.networkMb)}</p>
          <Delta value={kpis.networkDelta} />
        </article>
        <article className="an-card an-kpi">
          <div className="an-kpi__top">
            <span className="an-ico an-ico--purple">
              <Layers size={18} aria-hidden="true" />
            </span>
            <Sparkline values={kpis.apiSpark} tone="purple" />
          </div>
          <p className="an-kpi__label">API calls</p>
          <p className="an-kpi__value">{formatInt(kpis.apiCalls)}</p>
          <Delta value={kpis.apiDelta} />
        </article>
        <article className="an-card an-kpi">
          <div className="an-kpi__top">
            <span className="an-ico an-ico--orange">
              <Clock size={18} aria-hidden="true" />
            </span>
            <Sparkline values={kpis.timeSpark} tone="orange" />
          </div>
          <p className="an-kpi__label">Mocking time</p>
          <p className="an-kpi__value">{formatDuration(kpis.mockingMinutes)}</p>
          <Delta value={kpis.mockingDelta} />
        </article>
        <article className="an-card an-kpi">
          <div className="an-kpi__top">
            <span className="an-ico an-ico--blue">
              <RefreshCw size={18} aria-hidden="true" />
            </span>
            <Sparkline values={kpis.syncSpark} tone="blue" />
          </div>
          <p className="an-kpi__label">Completed syncs</p>
          <p className="an-kpi__value">{formatInt(kpis.completedSyncs)}</p>
          <p className="an-success">{formatPct(kpis.syncSuccessPct)} successful</p>
        </article>
      </div>

      <div className="an-split">
        <section className="an-card">
          <div className="an-card__head">
            <div>
              <div className="an-title">
                <h2>Network usage over time</h2>
                <Hint
                  label="About network usage"
                  text="Data transferred while MockLoc runs, split by Wi-Fi and mobile."
                />
              </div>
              <p className="an-sub">Data transferred while MockLoc runs, split by Wi-Fi and mobile.</p>
            </div>
            <ul className="an-key">
              <li>
                <i className="an-dot an-tone an-tone--green" />
                Mobile data
              </li>
              <li>
                <i className="an-dot an-tone an-tone--blue" />
                Wi-Fi & other
              </li>
            </ul>
          </div>
          <UsageChart points={snapshot.usage} axisKb={snapshot.usageAxisKb} />
        </section>

        <section className="an-card">
          <div className="an-card__head">
            <div>
              <div className="an-title">
                <h2>API traffic by category</h2>
                <Hint label="About API traffic" text="API calls and data used per category." />
              </div>
              <p className="an-sub">API calls and data used per category.</p>
            </div>
            <div className="an-seg" role="group" aria-label="API traffic metric">
              <button type="button" aria-pressed={traffic === 'calls'} onClick={() => onTraffic('calls')}>
                Calls
              </button>
              <button type="button" aria-pressed={traffic === 'data'} onClick={() => onTraffic('data')}>
                Data
              </button>
            </div>
          </div>
          <DonutChart
            slices={slices}
            centerValue={traffic === 'calls' ? formatInt(trafficTotal) : trafficTotal.toFixed(2)}
            centerCaption={traffic === 'calls' ? 'calls' : 'MB'}
          />
        </section>
      </div>

      <div className="an-split an-split--even">
        <section className="an-card">
          <div className="an-card__head">
            <div>
              <div className="an-title">
                <h2>Mocking time by module</h2>
                <Hint
                  label="About mocking time"
                  text="Active mocking time per module. See how long each module ran in this period."
                />
              </div>
              <p className="an-sub">Active mocking time per module — see how long each module ran in this period.</p>
            </div>
            <div className="an-seg" role="group" aria-label="Module metric">
              <button type="button" aria-pressed={moduleMode === 'time'} onClick={() => onModuleMode('time')}>
                Time
              </button>
              <button type="button" aria-pressed={moduleMode === 'sessions'} onClick={() => onModuleMode('sessions')}>
                Sessions
              </button>
            </div>
          </div>
          <ul className="an-mods" key={moduleMode}>
            {snapshot.modules.map((row, index) => {
              const share = moduleShares[index] || 0;
              const width = maxShare > 0 ? (share / maxShare) * 100 : 0;
              return (
                <li key={row.id} className="an-mod">
                  <span className="an-mod__name" title={row.label}>
                    {row.label}
                  </span>
                  <span className="an-mod__track">
                    <span
                      className={`an-mod__fill an-tone an-tone--${row.tone}`}
                      style={{ width: `${width}%` }}
                    />
                  </span>
                  <span className="an-mod__time">
                    {moduleMode === 'time' ? formatDuration(row.minutes) : `${row.sessions} sessions`}
                  </span>
                  <span className="an-mod__pct">{formatPct(share)}</span>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="an-card">
          <div className="an-card__head">
            <div className="an-title">
              <h2>Sync activity</h2>
              <Hint label="About sync activity" text="Completed syncs by type." />
            </div>
            <ul className="an-key">
              {SYNC_SERIES.map((series) => (
                <li key={series.key}>
                  <i className={`an-dot an-tone an-tone--${series.tone}`} />
                  {series.label}
                </li>
              ))}
            </ul>
          </div>
          <SyncChart days={snapshot.syncs} axisMax={snapshot.syncAxis} />
          {snapshot.latest && (
            <div className="an-latest-wrap">
              <p className="an-kicker">Latest sync activity</p>
              <button
                type="button"
                className="an-latest"
                aria-expanded={syncOpen}
                onClick={onToggleSync}
              >
                <span className="an-latest__mark" aria-hidden="true">
                  {snapshot.latest.title.charAt(0)}
                </span>
                <span className="an-latest__copy">
                  <strong>{snapshot.latest.title}</strong>
                  <small>{snapshot.latest.when}</small>
                </span>
                <span className="an-latest__side">
                  <span className={`an-pill${snapshot.latest.status === 'Failed' ? ' an-pill--bad' : ''}`}>
                    {snapshot.latest.status}
                  </span>
                  {syncOpen ? (
                    <ChevronDown size={16} aria-hidden="true" />
                  ) : (
                    <ChevronRight size={16} aria-hidden="true" />
                  )}
                </span>
              </button>
              {syncOpen && (
                <dl className="an-latest__more">
                  <div>
                    <dt>Device</dt>
                    <dd>{snapshot.latest.device}</dd>
                  </div>
                  <div>
                    <dt>Duration</dt>
                    <dd>{snapshot.latest.duration}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>{snapshot.latest.status}</dd>
                  </div>
                  <div>
                    <dt>Note</dt>
                    <dd>{snapshot.latest.detail}</dd>
                  </div>
                </dl>
              )}
            </div>
          )}
        </section>
      </div>

      <div className="an-split an-split--pair">
        <section className="an-card">
          <div className="an-title">
            <h2>Resource usage</h2>
            <Hint
              label="About resource usage"
              text="Local device resource usage during MockLoc activity."
            />
          </div>
          <p className="an-sub">Local device resource usage during MockLoc activity.</p>
          <div className="an-minis">
            <article className="an-mini an-mini--green">
              <span className="an-ico an-ico--green">
                <Battery size={18} aria-hidden="true" />
              </span>
              <p className="an-mini__label">Battery-active sessions</p>
              <p className="an-mini__value">{formatInt(resources.batterySessions)}</p>
              <p className="an-mini__text">Sessions that used significant battery while mocking.</p>
            </article>
            <article className="an-mini an-mini--purple">
              <span className="an-ico an-ico--purple">
                <Database size={18} aria-hidden="true" />
              </span>
              <p className="an-mini__label">Local data events</p>
              <p className="an-mini__value">{formatInt(resources.localEvents)}</p>
              <p className="an-mini__text">Location points, cache and other local data events.</p>
            </article>
            <article className="an-mini an-mini--blue">
              <span className="an-ico an-ico--blue">
                <BarChart3 size={18} aria-hidden="true" />
              </span>
              <p className="an-mini__label">Total data used</p>
              <p className="an-mini__value">{formatMb(totalData)}</p>
              <p className="an-mini__text">
                {formatMb(resources.mobileMb)} mobile · {formatMb(resources.wifiMb)} Wi-Fi
              </p>
            </article>
          </div>
        </section>

        <section className="an-card">
          <div className="an-title">
            <h2>Insights</h2>
            <Hint label="About insights" text="Key insights from your MockLoc activity in this period." />
          </div>
          <p className="an-sub">Key insights from your MockLoc activity in this period.</p>
          <div className="an-minis">
            <article className="an-mini an-mini--purple">
              <span className="an-ico an-ico--purple">
                <Clock size={18} aria-hidden="true" />
              </span>
              <p className="an-mini__label">Peak API activity</p>
              <p className="an-mini__value">{insights.peakLabel}</p>
              <p className="an-mini__text">{insights.peakText}</p>
            </article>
            <article className="an-mini an-mini--green">
              <span className="an-ico an-ico--green">
                <Gamepad2 size={18} aria-hidden="true" />
              </span>
              <p className="an-mini__label">Most used module</p>
              <p className="an-mini__value">{insights.moduleLabel}</p>
              <p className="an-mini__text">{insights.moduleText}</p>
            </article>
            <article className="an-mini an-mini--blue">
              <span className="an-ico an-ico--blue">
                <Wifi size={18} aria-hidden="true" />
              </span>
              <p className="an-mini__label">Mobile data</p>
              <p className="an-mini__value">{formatMb(insights.mobileMb)}</p>
              <p className="an-mini__text">{insights.mobileText}</p>
            </article>
          </div>
        </section>
      </div>
    </>
  );
}

const AnalyticsView: React.FC<AnalyticsViewProps> = ({ loading: loadingProp, data }) => {
  const autoLoading = usePreviewLoading(loadingProp === undefined);
  const loading = loadingProp ?? autoLoading;
  const [period, setPeriod] = useState<AnalyticsPeriod>('7d');
  const [custom, setCustom] = useState({ start: WEEK_START, end: WEEK_END });
  const [draft, setDraft] = useState(custom);
  const [dateOpen, setDateOpen] = useState(false);
  const [traffic, setTraffic] = useState<TrafficMode>('calls');
  const [moduleMode, setModuleMode] = useState<ModuleMode>('time');
  const [syncOpen, setSyncOpen] = useState(false);
  const [exported, setExported] = useState(false);
  const dateRef = useRef<HTMLDivElement>(null);
  const startId = React.useId();
  const endId = React.useId();

  const local = useMemo(() => snapshotFor(period, custom), [period, custom]);
  const shown = data === undefined ? local : data;
  const rangeLabel = shown?.rangeLabel || local.rangeLabel;
  const isEmpty = !loading && (shown == null || shown.empty);

  useEffect(() => {
    setSyncOpen(false);
  }, [rangeLabel]);

  useEffect(() => {
    if (!exported) return undefined;
    const id = window.setTimeout(() => setExported(false), 1600);
    return () => window.clearTimeout(id);
  }, [exported]);

  useEffect(() => {
    if (!dateOpen) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (dateRef.current && !dateRef.current.contains(event.target as Node)) {
        setDateOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDateOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [dateOpen]);

  const openDates = () => {
    const source = shown ?? local;
    setDraft({ start: source.start, end: source.end });
    setDateOpen(true);
  };

  const selectPeriod = (next: AnalyticsPeriod) => {
    if (next === 'custom') {
      openDates();
      return;
    }
    setPeriod(next);
    setDateOpen(false);
  };

  const applyDates = () => {
    if (!draft.start || !draft.end) return;
    const next = customSnapshot(draft.start, draft.end);
    setCustom({ start: next.start, end: next.end });
    setPeriod('custom');
    setDateOpen(false);
  };

  const showSample = () => {
    setPeriod('7d');
    setDateOpen(false);
  };

  const onExport = () => {
    if (!shown) return;
    downloadText(`mockloc-analytics-${shown.start}-to-${shown.end}.csv`, analyticsCsv(shown));
    setExported(true);
  };

  return (
    <div className="an" data-testid="analytics-screen" aria-busy={loading}>
      {loading && (
        <p className="visually-hidden" role="status">
          Loading analytics
        </p>
      )}
      <p className="an-intro">{INTRO}</p>
      <div className="an-toolbar" role="region" aria-label="Analytics range">
        <div className="an-datewrap" ref={dateRef}>
          <button
            type="button"
            className="an-btn an-date"
            aria-expanded={dateOpen}
            aria-haspopup="dialog"
            aria-label={`Date range, ${rangeLabel}`}
            disabled={loading}
            onClick={() => (dateOpen ? setDateOpen(false) : openDates())}
          >
            <Calendar size={16} aria-hidden="true" />
            <span>{rangeLabel}</span>
            <ChevronDown size={16} aria-hidden="true" />
          </button>
          {dateOpen && (
            <div className="an-pop" role="dialog" aria-label="Custom date range">
              <p className="an-pop__hint">
                Sample data covers {formatRange(SAMPLE_START, SAMPLE_END)}. Other dates show the empty state.
              </p>
              <label htmlFor={startId}>
                Start date
                <input
                  id={startId}
                  type="date"
                  value={draft.start}
                  onChange={(event) => setDraft((current) => ({ ...current, start: event.target.value }))}
                />
              </label>
              <label htmlFor={endId}>
                End date
                <input
                  id={endId}
                  type="date"
                  value={draft.end}
                  onChange={(event) => setDraft((current) => ({ ...current, end: event.target.value }))}
                />
              </label>
              <button
                type="button"
                className="an-primary an-pop__apply"
                disabled={!draft.start || !draft.end}
                onClick={applyDates}
              >
                Apply range
              </button>
            </div>
          )}
        </div>
        <div className="an-periods" role="group" aria-label="Range length">
          {PERIODS.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={period === item.id}
              disabled={loading}
              onClick={() => selectPeriod(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <button type="button" className="an-btn an-export" disabled={loading || !shown} onClick={onExport}>
          {exported ? <Check size={16} aria-hidden="true" /> : <Download size={16} aria-hidden="true" />}
          {exported ? 'Exported' : 'Export'}
        </button>
      </div>

      {loading && <Skeletons />}
      {isEmpty && <EmptyState rangeLabel={rangeLabel} onSample={data === undefined ? showSample : undefined} />}
      {!loading && shown && !shown.empty && (
        <AnalyticsBody
          key={`${shown.start}:${shown.end}`}
          snapshot={shown}
          traffic={traffic}
          moduleMode={moduleMode}
          syncOpen={syncOpen}
          onTraffic={setTraffic}
          onModuleMode={setModuleMode}
          onToggleSync={() => setSyncOpen((open) => !open)}
        />
      )}
    </div>
  );
};

export default AnalyticsView;
